import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SegmentedControl } from '@/components/ui/segmented-control'
import {
  AutoRefreshControl, SLOW_REFRESH_INTERVALS, useRefreshInterval,
} from '@/components/ui/auto-refresh-control'
import { exportElementToPdf } from '@/lib/exportElementToPdf'
import { postRecoveryApi, type PostRecoveryTask } from './api'
import { RunVarianceTable } from './RunVarianceTable'
import { PlannedVsActualGantt } from './PlannedVsActualGantt'
import { NotRunPanel } from './NotRunPanel'
import { RunbookHistoryView } from './RunbookHistoryView'
import { TaskCommentDialog } from './TaskCommentDialog'

/**
 * Post recovery — what the plan said, what actually happened, and what to change next time.
 *
 * FOUR QUESTIONS, ONE PER VIEW, and they are deliberately not one screen. A recovery manager
 * asking "how did we do" wants the run table; an engineer asking "where did the time go" wants
 * one run's timeline; a vault owner asking "what did we fail to recover" wants the reasons; and
 * the person tuning a runbook wants the same object across every recovery it has had. Stacking
 * all four vertically would bury the last one, which is the one that changes anything.
 *
 * The history view is scoped to an OBJECT, not to this set, because the whole point of it is to
 * look across recovery sets. Choosing a run in the overview picks the object it examines.
 */

type View = 'overview' | 'timeline' | 'notrun' | 'history'

export function PostRecoveryTab({ setId }: { setId: number }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const sheet = useRef<HTMLDivElement>(null)

  const [view, setView] = useState<View>('overview')
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null)
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null)
  const [commentTaskId, setCommentTaskId] = useState<number | null>(null)
  const [exporting, setExporting] = useState(false)

  const [refresh, setRefresh] = useRefreshInterval(
    'post-recovery.refresh', 300_000, SLOW_REFRESH_INTERVALS,
  )

  const overview = useQuery({
    queryKey: ['post-recovery-set', setId],
    queryFn: () => postRecoveryApi.setOverview(setId),
    refetchInterval: refresh,
    enabled: Number.isInteger(setId) && setId > 0,
  })

  /** Picking a run picks its object too — the two views are two readings of one choice. */
  const selectRun = (requestId: number) => {
    setSelectedRequestId(requestId)
    const run = overview.data?.runs.find((r) => r.requestId === requestId)
    if (run) setSelectedObjectId(run.objectId)
    setView('timeline')
  }

  const exportPdf = async () => {
    setExporting(true)
    try {
      await exportElementToPdf({
        el: sheet.current,
        filename: `post-recovery-set-${setId}-${view}.pdf`,
        title: t('postRecovery.title', 'Post recovery'),
        subtitle: VIEWS.find((v) => v.value === view)?.label ?? view,
      })
    } finally {
      setExporting(false)
    }
  }

  const VIEWS: { value: View; label: string }[] = [
    { value: 'overview', label: t('postRecovery.viewOverview', 'Runs') },
    { value: 'timeline', label: t('postRecovery.viewTimeline', 'Plan vs actual') },
    { value: 'notrun', label: t('postRecovery.viewNotRun', 'Never ran') },
    { value: 'history', label: t('postRecovery.viewHistory', 'Runbook history') },
  ]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t('postRecovery.title', 'Post recovery')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('postRecovery.subtitle',
              'What the plan said against what happened, and what the next recovery should assume instead.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AutoRefreshControl
            value={refresh}
            onChange={setRefresh}
            onRefresh={() => { void queryClient.invalidateQueries({ queryKey: ['post-recovery-set', setId] }) }}
            isRefreshing={overview.isFetching}
            updatedAt={overview.dataUpdatedAt}
            intervals={SLOW_REFRESH_INTERVALS}
          />
          <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                  onClick={() => void exportPdf()} disabled={exporting}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {exporting ? t('common.exporting', 'Exporting…') : t('common.exportPdf', 'Export PDF')}
          </Button>
        </div>
      </div>

      <SegmentedControl
        size="sm"
        ariaLabel={t('postRecovery.viewLabel', 'Post-recovery view')}
        value={view}
        onValueChange={(v) => setView(v as View)}
        options={VIEWS}
      />

      <div ref={sheet} className="space-y-4">
        {view === 'overview' && (
          <RunVarianceTable
            setId={setId}
            selectedRequestId={selectedRequestId}
            onSelectRun={selectRun}
          />
        )}

        {view === 'timeline' && (
          selectedRequestId == null ? (
            <ChooseARun
              runs={overview.data?.runs ?? []}
              onPick={selectRun}
              emptyHint={t('postRecovery.noRunsYet',
                'No runbook has been instantiated for this set yet, so there is nothing to compare.')}
              prompt={t('postRecovery.pickRun', 'Choose a recovery to see its plan against what happened.')}
            />
          ) : (
            <PlannedVsActualGantt
              requestId={selectedRequestId}
              onOpenComments={(task: PostRecoveryTask) => setCommentTaskId(task.id)}
            />
          )
        )}

        {view === 'notrun' && <NotRunPanel setId={setId} />}

        {view === 'history' && (
          selectedObjectId == null ? (
            <ChooseARun
              runs={overview.data?.runs ?? []}
              onPick={selectRun}
              emptyHint={t('postRecovery.noRunsYet',
                'No runbook has been instantiated for this set yet, so there is nothing to compare.')}
              prompt={t('postRecovery.pickObject',
                'Choose an object to see how its runbook has performed across every recovery it has had.')}
            />
          ) : (
            <RunbookHistoryView objectId={selectedObjectId} />
          )
        )}
      </div>

      <TaskCommentDialog
        open={commentTaskId != null}
        onOpenChange={(open) => { if (!open) setCommentTaskId(null) }}
        taskId={commentTaskId}
      />
    </div>
  )
}

/**
 * Both the timeline and the history need something chosen first. Offering the list here rather
 * than auto-selecting the first run keeps the reader in control of which recovery they are
 * looking at — picking one for them is how somebody ends up reading the wrong object's numbers.
 */
function ChooseARun({ runs, onPick, prompt, emptyHint }: {
  runs: { requestId: number; objectId: string; description?: string | null; taskCount: number; tasksMeasured: number }[]
  onPick: (requestId: number) => void
  prompt: string
  emptyHint: string
}) {
  const { t } = useTranslation()
  if (runs.length === 0) {
    return <div className="rounded-xl border p-8 text-center text-sm text-muted-foreground">{emptyHint}</div>
  }
  return (
    <div className="rounded-xl border p-4">
      <p className="mb-3 text-sm text-muted-foreground">{prompt}</p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {runs.map((r) => (
          <button
            key={r.requestId}
            type="button"
            onClick={() => onPick(r.requestId)}
            className="rounded-lg border px-3 py-2 text-left text-sm hover:bg-muted/60"
          >
            <div className="font-medium">{r.objectId}</div>
            {r.description && <div className="truncate text-xs text-muted-foreground">{r.description}</div>}
            <div className="mt-1 text-xs tabular-nums text-muted-foreground">
              {t('postRecovery.measuredOf', '{{measured}} of {{total}} tasks measured',
                { measured: r.tasksMeasured, total: r.taskCount })}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
